import { API_BASE_URL } from './api';

// Follow a test run to completion.
//
// Starting a run answers immediately with an execution id - the run itself
// takes minutes and nobody holds an HTTP request open for that. This asks the
// server "is it done yet?" until it says so.
//
// It replaces a fixed set of timed refreshes that assumed every run finished
// within 90 seconds: a longer suite was abandoned mid-flight and the row went
// back to looking idle while the test was still going.

const POLL_INTERVAL_MS = 3000;
// A run that has not finished in 30 minutes is not coming back; the suite
// budget is well under that, so stop asking rather than poll for ever.
const POLL_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * @param {string} executionId  id returned when the run was started
 * @param {object} [options]
 * @param {function} [options.onTick]  called with each status, for the UI
 * @returns {Promise<{status: string, error: string|null, timedOut?: boolean}>}
 */
export function waitForExecution(executionId, options = {}) {
  const { onTick } = options;

  return new Promise((resolve) => {
    if (!executionId) {
      resolve({ status: 'unknown', error: null });
      return;
    }

    const startedAt = Date.now();
    let timer = null;

    const stop = (result) => {
      if (timer) clearTimeout(timer);
      resolve(result);
    };

    const check = async () => {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        stop({ status: 'timeout', error: 'The run did not finish in time', timedOut: true });
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/executions/${executionId}`);
        const data = await response.json();

        if (onTick) onTick(data);

        // "unknown" means the server restarted or the entry was cleaned up.
        // Either way nothing is running under this id any more.
        if (!data.running) {
          stop({ status: data.status || 'unknown', error: data.error || null });
          return;
        }
      } catch (error) {
        // A dropped request is not the end of the run - the server is still
        // working. Try again on the next tick.
        console.warn('[EXECUTION] Status check failed, will retry:', error.message);
      }

      timer = setTimeout(check, POLL_INTERVAL_MS);
    };

    timer = setTimeout(check, POLL_INTERVAL_MS);
  });
}
